import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, TrendingUp } from "lucide-react";
import { formatUS } from "@/lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Sparkline-style chart, no chart lib. Plots height & weight over time. */
const ProgressChart = ({ measurements }) => {
  const data = useMemo(() => {
    const m = measurements.filter((x) => x.date).slice().sort((a, b) => a.date.localeCompare(b.date));
    return m;
  }, [measurements]);

  if (data.length < 2) return null;

  const W = 520, H = 140, PAD = 28;
  const xs = data.map((d) => new Date(d.date).getTime());
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const xSpan = xMax === xMin ? 1 : xMax - xMin;
  const xAt = (t) => PAD + ((t - xMin) / xSpan) * (W - PAD * 2);

  const series = (key, color, unitKey) => {
    const pts = data.map((d) => ({ t: new Date(d.date).getTime(), v: d[key], u: d[unitKey] })).filter((p) => p.v != null);
    if (pts.length < 2) return null;
    const vs = pts.map((p) => p.v);
    const vMin = Math.min(...vs);
    const vMax = Math.max(...vs);
    const vSpan = vMax === vMin ? 1 : vMax - vMin;
    const yAt = (v) => H - PAD - ((v - vMin) / vSpan) * (H - PAD * 2);
    const d = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(p.t).toFixed(1)} ${yAt(p.v).toFixed(1)}`).join(" ");
    return { d, pts: pts.map((p) => ({ x: xAt(p.t), y: yAt(p.v), v: p.v, u: p.u, t: p.t })), color, key };
  };

  const h = series("height", "#2563EB", "height_unit");
  const w = series("weight", "#DC2626", "weight_unit");

  return (
    <div className="bg-white border border-[var(--app-border)] rounded p-3" data-testid="measurements-chart">
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-[var(--app-text-soft)] mb-1">
        <TrendingUp className="w-3 h-3" />
        {h && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ backgroundColor: h.color }} /> Height</span>}
        {w && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ backgroundColor: w.color }} /> Weight</span>}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32">
        {[h, w].filter(Boolean).map((s) => (
          <g key={s.key}>
            <path d={s.d} fill="none" stroke={s.color} strokeWidth="2" />
            {s.pts.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r="3" fill="#fff" stroke={s.color} strokeWidth="2">
                <title>{`${new Date(p.t).toISOString().slice(0, 10)} — ${p.v} ${p.u || ""}`}</title>
              </circle>
            ))}
          </g>
        ))}
      </svg>
    </div>
  );
};

const numOrNull = (v) => {
  if (v === "" || v == null) return null;
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
};

export const MeasurementsLog = ({ animalId, measurements = [], onChange }) => {
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), height: "", weight: "", notes: "" });
  const [busy, setBusy] = useState(false);
  // Units used for new entries; existing rows keep their original units.
  const [heightUnit, setHeightUnit] = useState(() => measurements.find((m) => m.height_unit)?.height_unit || "cm");
  const [weightUnit, setWeightUnit] = useState(() => measurements.find((m) => m.weight_unit)?.weight_unit || "kg");

  const sorted = useMemo(
    () => [...measurements].sort((a, b) => (a.date || "").localeCompare(b.date || "")),
    [measurements],
  );

  const add = async () => {
    if (!form.date) { toast.error("Date is required"); return; }
    const payload = {
      date: form.date,
      height: numOrNull(form.height),
      weight: numOrNull(form.weight),
      height_unit: heightUnit,
      weight_unit: weightUnit,
      notes: form.notes || "",
    };
    if (payload.height == null && payload.weight == null) {
      toast.error("Enter at least height or weight");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/animals/${animalId}/measurements`, payload);
      onChange([...measurements, r.data].sort((a, b) => (a.date || "").localeCompare(b.date || "")));
      setForm({ date: new Date().toISOString().slice(0, 10), height: "", weight: "", notes: "" });
      toast.success("Measurement saved");
    } catch { toast.error("Couldn't save measurement"); }
    setBusy(false);
  };

  const remove = async (mid) => {
    try {
      await axios.delete(`${API}/animals/${animalId}/measurements/${mid}`);
      onChange(measurements.filter((m) => m.id !== mid));
    } catch { toast.error("Couldn't delete"); }
  };

  return (
    <div className="space-y-3" data-testid="measurements-log">
      {/* Add row */}
      <div className="bg-white border border-[var(--app-border)] rounded p-3 grid grid-cols-1 md:grid-cols-[150px_1fr_1fr_1fr_auto] gap-2 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">Date</Label>
          <Input type="date" className="font-mono-tech" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="meas-date" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">Height ({heightUnit})</Label>
          <Input type="number" step="0.1" className="font-mono-tech" value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} data-testid="meas-height" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">Weight ({weightUnit})</Label>
          <Input type="number" step="0.1" className="font-mono-tech" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} data-testid="meas-weight" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">Note</Label>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="optional" data-testid="meas-notes" />
        </div>
        <Button onClick={add} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="meas-add">
          <Plus className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Add"}
        </Button>
      </div>

      {/* Chart */}
      <ProgressChart measurements={sorted} />

      {/* Table */}
      {sorted.length === 0 ? (
        <div className="text-sm text-[var(--app-text-soft)] py-2">No measurements yet — add weekly checks to graph growth.</div>
      ) : (
        <div className="bg-white border border-[var(--app-border)] rounded overflow-hidden">
          <table className="w-full text-sm" data-testid="meas-table">
            <thead className="bg-[var(--app-bg)] text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-right px-3 py-2">Height</th>
                <th className="text-right px-3 py-2">Weight</th>
                <th className="text-left px-3 py-2">Note</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((m) => (
                <tr key={m.id} className="border-t border-[var(--app-border)]" data-testid={`meas-row-${m.id}`}>
                  <td className="px-3 py-1.5 font-mono-tech">{formatUS(m.date)}</td>
                  <td className="px-3 py-1.5 text-right font-mono-tech">{m.height != null ? `${m.height} ${m.height_unit || ""}` : "—"}</td>
                  <td className="px-3 py-1.5 text-right font-mono-tech">{m.weight != null ? `${m.weight} ${m.weight_unit || ""}` : "—"}</td>
                  <td className="px-3 py-1.5 text-[var(--app-text-soft)] truncate max-w-[200px]">{m.notes || ""}</td>
                  <td className="px-3 py-1.5">
                    <button type="button" onClick={() => remove(m.id)} className="text-red-500 hover:text-red-700 p-0.5" aria-label="Delete" data-testid={`meas-del-${m.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Unit selector */}
      <div className="flex gap-3 text-[10px] text-[var(--app-text-soft)]">
        <span className="uppercase tracking-[0.18em]">Units:</span>
        <label className="flex items-center gap-1 cursor-pointer">
          Height
          <Select value={heightUnit} onValueChange={setHeightUnit}>
            <SelectTrigger className="h-7 text-[11px] w-[70px]" data-testid="meas-height-unit"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="cm">cm</SelectItem><SelectItem value="in">in</SelectItem></SelectContent>
          </Select>
        </label>
        <label className="flex items-center gap-1 cursor-pointer">
          Weight
          <Select value={weightUnit} onValueChange={setWeightUnit}>
            <SelectTrigger className="h-7 text-[11px] w-[70px]" data-testid="meas-weight-unit"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="kg">kg</SelectItem><SelectItem value="lb">lb</SelectItem></SelectContent>
          </Select>
        </label>
      </div>
    </div>
  );
};

export default MeasurementsLog;
